import { useSpaceIDFromPath } from '@/data/space/hooks';
import {
  useGetPageQuery,
  useGetSpaceQuery,
  useListCollectionItemsQuery,
  useListPagesQuery,
} from '@/store';
import { checkRouteChildren } from '@/utils/checkRouteChildren';
import { ArrowRight, Article, CaretRight, File, WarningCircle } from '@phosphor-icons/react';
import { IPage, ISpaceRoute, validUUID } from '@typedream/data';
import { Button } from '@typedream/ui';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MenuNav } from '../Nav';
import { BlogItemProps } from './types';

function filterItemPageTemplate(page: IPage): boolean {
  const { customization } = page;
  return customization && 'item_page_data' in customization;
}

function BlogItem({ page }: BlogItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [blogItems, setBlogItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const { data: spaceData } = useGetSpaceQuery([String(page?.space_id)], {
    skip: !page || !page?.space_id,
  });

  const pageInSpaceData = useMemo(() => {
    let found = null;

    checkRouteChildren(spaceData?.routes?.children || {}, ([, route]) => {
      if (route.page_id === page?.id) {
        found = route;
      }
    });

    return found;
  }, [page?.id, spaceData]);

  const { data: blogParentTemplatePageData } = useGetPageQuery(
    [String(page?.space_id), String(pageInSpaceData?.children_template_page_id)],
    {
      skip:
        !spaceData ||
        !page ||
        !page?.space_id ||
        !pageInSpaceData ||
        !validUUID(pageInSpaceData?.children_template_page_id),
    }
  );

  const recordEntriesByPageId = useCallback(
    (children: { [slug: string]: ISpaceRoute }): [Array<[string, ISpaceRoute]>, boolean] => {
      let entrySequence: Array<[string, ISpaceRoute]> = [];
      let found = false;

      // eslint-disable-next-line no-restricted-syntax
      for (const [slug, route] of Object.entries(children)) {
        if (Object.keys(route.children || {}).length > 0) {
          const [childEntry, childFound] = recordEntriesByPageId(route.children || {});
          entrySequence = [...childEntry, ...entrySequence];
          found = childFound || found;
        }

        if (route.page_id === page?.id) {
          found = true;
        }

        if (found) {
          return [[[slug, route], ...entrySequence], found];
        }
      }

      return [entrySequence, found];
    },
    [page?.id]
  );

  const getPageCollectionItem = useCallback(
    async (p: IPage) => {
      setIsError(false);
      setIsLoading(true);
      if (p?.space_id && p?.customization?.collection_data) {
        try {
          const res = await useListCollectionItemsQuery
            .run([
              String(page?.space_id),
              blogParentTemplatePageData?.customization?.item_page_data,
              '',
            ])
            .unwrap();
          setBlogItems(res?.data ?? []);
        } catch (err) {
          setIsError(true);
        }
      }
      setIsLoading(false);
    },
    [page?.space_id, blogParentTemplatePageData?.customization?.item_page_data]
  );

  useEffect(() => {
    if (page) {
      getPageCollectionItem(page);
    }
  }, [page, getPageCollectionItem]);

  const getBlogItemTitle = (blog: { [field: string]: any }) => {
    if (blogParentTemplatePageData?.customization?.item_page_data?.contentSchema) {
      return blog?.[blogParentTemplatePageData.customization.item_page_data.contentSchema.title];
    }

    return '';
  };

  return (
    <>
      <MenuNav
        href={`/s/${spaceData?.slug}/${spaceData?.id}/${page?.id}`}
        pre={
          <button
            type="button"
            className={classNames(
              'flex items-center text-secondary bg-transparent hover:bg-neutral-300 rounded-full transition-all p-0.5 border-0'
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen((currentValue) => !currentValue);
            }}
          >
            <CaretRight
              size={16}
              weight="bold"
              className={classNames('transition-all ease-out duration-200', {
                'rotate-90': isOpen,
              })}
            />
          </button>
        }
        post={
          <>
            {isLoading && (
              <div className="justify-self-end">
                <div className="loader-box">
                  <div className="loader is-loading" />
                </div>
              </div>
            )}
            {isError && (
              <div className="justify-self-end">
                <WarningCircle size={16} weight="bold" color="var(--color-text-danger)" />
              </div>
            )}
          </>
        }
      >
        <span className="flex items-center gap-2">
          <Article size={16} weight="bold" className="text-secondary" />
          {page.name}
        </span>
      </MenuNav>

      {isOpen && isError && (
        <div className="flex gap-2.5 bg-neutral-100 ml-[42px] rounded-[14px] p-3">
          <div>
            <WarningCircle size={16} color="var(--red-400, red)" />
          </div>
          <div>
            <p className="text-xs text-neutral-600 leading-[18px]">
              Can&apos;t load items for {page?.name}
            </p>

            <Button variant="ghost" onClick={() => getPageCollectionItem(page)}>
              <span>Try Again</span>
              <span>
                <ArrowRight size={16} weight="bold" />
              </span>
            </Button>
          </div>
        </div>
      )}
      {isOpen && !isError && (
        <div className="pl-5">
          {blogItems.map((blog, idx) => (
            <MenuNav
              key={blog?.id ?? idx}
              href={`/col/${spaceData?.slug}/${spaceData?.id}/${blogParentTemplatePageData?.id}/${blog?.id}`}
              pre={<File size={16} weight="bold" className="text-secondary" />}
            >
              {getBlogItemTitle(blog)}
            </MenuNav>
          ))}
        </div>
      )}
    </>
  );
}

export function MenuBlogs() {
  const spaceID = useSpaceIDFromPath();
  const { data: spaceData } = useGetSpaceQuery([String(spaceID)], {
    skip: !spaceID,
  });
  const { data: pagesData } = useListPagesQuery([String(spaceID)], {
    skip: !spaceID,
  });
  const blogContent = useMemo(() => {
    let pages: Array<IPage> | undefined;
    if (pagesData) {
      pages = Object.values(pagesData)
        .filter((p) => !filterItemPageTemplate(p))
        .filter((p: IPage) => {
          const subPage = Object.values(spaceData?.routes?.children || {}).find(
            (value) => p.id === value.page_id
          );
          if (!subPage && p.id !== spaceData?.routes?.page_id) return false;
          const isBlog = subPage ? validUUID(subPage.children_template_page_id) : false;

          return isBlog;
        }) as Array<IPage>;
    }

    return pages;
  }, [pagesData, spaceData]);

  return (
    <div className="mb-[24px]">
      {blogContent?.map((item) => (
        <BlogItem key={item.id} page={item} />
      ))}
    </div>
  );
}
